import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import type { Company, Property } from "@/types/database";

interface PropertyContextValue {
  property: Property | null;
  company: Company | null;
  loading: boolean;
}

const PropertyContext = createContext<PropertyContextValue | null>(null);

export function PropertyProvider({ children }: { children: ReactNode }) {
  const { propertyId, companyId } = useAuth();

  const propertyQuery = useQuery({
    queryKey: ["property", propertyId],
    enabled: !!propertyId,
    queryFn: async (): Promise<Property | null> => {
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("id", propertyId!)
        .maybeSingle();
      if (error) throw error;
      return data as Property | null;
    },
  });

  const companyQuery = useQuery({
    queryKey: ["company", companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<Company | null> => {
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .eq("id", companyId!)
        .maybeSingle();
      if (error) throw error;
      return data as Company | null;
    },
  });

  const value = useMemo<PropertyContextValue>(
    () => ({
      property: propertyQuery.data ?? null,
      company: companyQuery.data ?? null,
      loading: propertyQuery.isLoading || companyQuery.isLoading,
    }),
    [
      propertyQuery.data,
      propertyQuery.isLoading,
      companyQuery.data,
      companyQuery.isLoading,
    ],
  );

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
}

export function useProperty() {
  const ctx = useContext(PropertyContext);
  if (!ctx) throw new Error("useProperty must be used inside <PropertyProvider>");
  return ctx;
}
